'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FlowData {
  source: string;
  target: string;
  value: number;
  color: string;
}

interface ActivityPulseSankeyProps {
  flows: FlowData[];
}

const ActivityPulseSankey = ({ flows }: ActivityPulseSankeyProps) => {
  const [hoveredFlow, setHoveredFlow] = useState<FlowData | null>(null);

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const totalValue = flows.reduce((sum, flow) => sum + flow.value, 0);

  return (
    <div className="bg-card border border-border rounded-md shadow-md h-full flex flex-col">
      <div className="px-6 py-4 border-b border-border bg-secondary">
        <div className="flex items-center gap-2">
          <Icon name="ArrowsRightLeftIcon" size={20} className="text-accent" />
          <h2 className="font-mono text-lg font-semibold text-foreground">Activity Pulse</h2>
        </div>
        <p className="font-sans text-sm text-text-secondary mt-1">Real-time Value Flow Visualization</p>
      </div>
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="space-y-4">
          {flows.map((flow, index) => {
            const percentage = (flow.value / totalValue) * 100;
            const isHovered = hoveredFlow?.source === flow.source && hoveredFlow?.target === flow.target;

            return (
              <div
                key={index}
                className="relative"
                onMouseEnter={() => setHoveredFlow(flow)}
                onMouseLeave={() => setHoveredFlow(null)}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-medium text-foreground">{flow.source}</span>
                    <Icon name="ArrowRightIcon" size={16} className="text-text-secondary" />
                    <span className="font-mono text-sm font-medium text-foreground">{flow.target}</span>
                  </div>
                  <span className="font-data text-sm text-text-secondary">{percentage.toFixed(1)}%</span>
                </div>
                <div className="relative h-8 bg-primary rounded overflow-hidden">
                  <div
                    className={`h-full rounded transition-all duration-500 ease-out ${
                      isHovered ? 'opacity-100' : 'opacity-80'
                    }`}
                    style={{
                      width: `${percentage}%`,
                      backgroundColor: flow.color,
                    }}
                  />
                  {isHovered && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="font-data text-sm font-semibold text-white drop-shadow-lg">
                        {formatCurrency(flow.value)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-center justify-between">
            <span className="font-mono text-sm font-medium text-text-secondary">Total Flow</span>
            <span className="font-data text-lg font-semibold text-foreground">
              {formatCurrency(totalValue)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityPulseSankey;